/**
 * TEST PAGE — Henrik CEO section layout explorations
 * Route: /about/henrik-test
 * Remove this file when no longer needed.
 */
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { aboutAsset } from "./aboutAssets";

const ceoPhoto = aboutAsset("henrik-02.jpg");
const scribble = aboutAsset("scribble.svg");

const shineSpin = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 200% 0; }
`;

const QUOTE =
  "\u201CComics are one of the most powerful storytelling formats in the world, but the way they\u2019re distributed and experienced hasn\u2019t kept up. GlobalComix is changing that. We\u2019re building a platform where fans can discover everything, creators can reach a global audience, and communities can form around the stories they love.\u201D";

const ATTRIBUTION = "\u2013 Henrik Rydberg, CEO";

/* ── Shared photo w/ shine border ── */
function HenrikPhoto({ width, radius = "16px" }: { width: string; radius?: string }) {
  return (
    <Box position="relative" flexShrink={0} w={width}>
      <Box
        position="relative"
        borderRadius={radius}
        overflow="hidden"
        isolation="isolate"
        border="1px solid rgba(255,255,255,0.20)"
        sx={{
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: radius,
            padding: "1px",
            background:
              "radial-gradient(transparent, transparent, rgba(255,255,255,0.5), transparent, transparent)",
            backgroundSize: "200% 200%",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            animation: `${shineSpin} 6s linear infinite`,
            pointerEvents: "none",
            zIndex: 3,
          },
        }}
      >
        <Image
          src={ceoPhoto}
          alt="Henrik Rydberg, CEO"
          w="100%"
          h="auto"
          display="block"
          boxShadow="0 8px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)"
          objectFit="cover"
        />
      </Box>
      <Image
        src={scribble}
        alt=""
        position="absolute"
        bottom="40px"
        left="-48px"
        w="306px"
        h="76px"
        zIndex={2}
        pointerEvents="none"
      />
    </Box>
  );
}

/* ── Section label ── */
function OptionLabel({ label, description }: { label: string; description: string }) {
  return (
    <Box mb="24px">
      <Text
        fontSize="14px"
        fontWeight="600"
        color="blaze.blaze"
        textTransform="uppercase"
        letterSpacing="0.05em"
      >
        {label}
      </Text>
      <Text fontSize="13px" color="dune.500" mt="4px">
        {description}
      </Text>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   OPTION 1 — Current (384px image, side-by-side)
   ───────────────────────────────────────────────────────── */
function Option1() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel label="Option 1 — Current" description="384px image, side-by-side layout. What we have today." />
      <Flex maxW="960px" gap="56px" align="center" direction={{ base: "column", lg: "row" }}>
        <HenrikPhoto width="384px" />
        <Flex direction="column" gap="24px">
          <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
            From the CEO
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700" lineHeight="1.7">
            {QUOTE}
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700">
            {ATTRIBUTION}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   OPTION 2 — Smaller image (280px), same layout
   ───────────────────────────────────────────────────────── */
function Option2() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 2 — Compact Side-by-Side"
        description="280px image, more room for quote. Clean and balanced."
      />
      <Flex maxW="960px" gap="56px" align="center" direction={{ base: "column", lg: "row" }}>
        <HenrikPhoto width="280px" />
        <Flex direction="column" gap="24px">
          <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
            From the CEO
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700" lineHeight="1.7">
            {QUOTE}
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700">
            {ATTRIBUTION}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   OPTION 3 — Centered vertical stack, smaller photo
   Large pull-quote energy, image above text
   ───────────────────────────────────────────────────────── */
function Option3() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 3 — Centered Stack"
        description="240px image centered above quote. Feels more editorial, like a magazine pull-quote."
      />
      <Flex maxW="680px" mx="auto" direction="column" align="center" gap="40px" textAlign="center">
        <HenrikPhoto width="240px" />
        <Flex direction="column" gap="20px" align="center">
          <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
            From the CEO
          </Text>
          <Text fontSize="22px" fontWeight="400" color="dune.700" lineHeight="1.7" fontStyle="italic">
            {QUOTE}
          </Text>
          <Text fontSize="18px" fontWeight="500" color="dune.500" mt="4px">
            {ATTRIBUTION}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   OPTION 4 — Image right, text left (flipped layout)
   Smaller image, quote takes the lead
   ───────────────────────────────────────────────────────── */
function Option4() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 4 — Quote-First (Flipped)"
        description="Quote on left, 260px image on right. Puts words first, Henrik second."
      />
      <Flex maxW="960px" gap="56px" align="center" direction={{ base: "column", lg: "row" }}>
        <Flex direction="column" gap="24px" flex="1">
          <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
            From the CEO
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700" lineHeight="1.7">
            {QUOTE}
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700">
            {ATTRIBUTION}
          </Text>
        </Flex>
        <HenrikPhoto width="260px" />
      </Flex>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   OPTION 5 — Large quote, tiny circular headshot
   Testimonial / startup style
   ───────────────────────────────────────────────────────── */
function Option5() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 5 — Testimonial Style"
        description="120px circular headshot with large quote. Minimal, modern, startup energy."
      />
      <Flex maxW="720px" mx="auto" direction="column" align="center" gap="32px" textAlign="center">
        {/* Big quotation mark */}
        <Text fontSize="72px" lineHeight="1" color="blaze.blaze" opacity={0.3} mt="-8px">
          &ldquo;
        </Text>
        <Text fontSize="24px" fontWeight="400" color="dune.700" lineHeight="1.7" mt="-40px">
          {QUOTE.replace("\u201C", "").replace("\u201D", "")}
        </Text>
        <Flex align="center" gap="16px" mt="8px">
          <Box
            w="120px"
            h="120px"
            borderRadius="full"
            overflow="hidden"
            flexShrink={0}
            border="2px solid rgba(255,255,255,0.15)"
            boxShadow="0 4px 20px rgba(0,0,0,0.4)"
            sx={{
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "full",
                padding: "2px",
                background:
                  "radial-gradient(transparent, transparent, rgba(255,255,255,0.5), transparent, transparent)",
                backgroundSize: "200% 200%",
                WebkitMask:
                  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                animation: `${shineSpin} 6s linear infinite`,
                pointerEvents: "none",
                zIndex: 3,
              },
            }}
          >
            <Image src={ceoPhoto} alt="Henrik Rydberg" w="100%" h="100%" objectFit="cover" />
          </Box>
          <Flex direction="column" align="flex-start">
            <Text fontSize="18px" fontWeight="500" color="dune.dune">
              Henrik Rydberg
            </Text>
            <Text fontSize="14px" color="dune.500">
              CEO, GlobalComix
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   OPTION 6 — Offset overlap, image bleeds into quote area
   ───────────────────────────────────────────────────────── */
function Option6() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 6 — Offset Overlap"
        description="300px image pulled left with negative margin, quote overlaps slightly. Editorial feel."
      />
      <Flex maxW="960px" align="flex-start" direction={{ base: "column", lg: "row" }} position="relative">
        <Box flexShrink={0} ml={{ base: 0, lg: "-24px" }} mt={{ base: 0, lg: "24px" }} zIndex={1}>
          <HenrikPhoto width="300px" />
        </Box>
        <Flex
          direction="column"
          gap="24px"
          flex="1"
          bg="charcoal.700"
          borderRadius="16px"
          p="40px"
          ml={{ base: 0, lg: "-40px" }}
          mt={{ base: "-24px", lg: "0" }}
          border="1px solid"
          borderColor="whiteAlpha.100"
          zIndex={2}
        >
          <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
            From the CEO
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700" lineHeight="1.7">
            {QUOTE}
          </Text>
          <Flex align="center" gap="12px">
            <Box w="32px" h="1px" bg="blaze.blaze" opacity={0.5} />
            <Text fontSize="16px" fontWeight="500" color="dune.500">
              Henrik Rydberg, CEO
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────
   OPTION 7 — Hybrid: Circular headshot left + quote right
   Testimonial feel (Option 5) with side-by-side (Option 4)
   ───────────────────────────────────────────────────────── */
function Option7a() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 7A — Circle Left + Quote Right (120px)"
        description="Circular headshot on the left, big quote on the right. Testimonial energy with side-by-side balance."
      />
      <Flex maxW="960px" gap="48px" align="center" direction={{ base: "column", lg: "row" }}>
        {/* Left: circular photo + name stacked */}
        <Flex direction="column" align="center" gap="16px" flexShrink={0}>
          <Box
            w="120px"
            h="120px"
            borderRadius="full"
            overflow="hidden"
            position="relative"
            border="2px solid rgba(255,255,255,0.15)"
            boxShadow="0 4px 20px rgba(0,0,0,0.4)"
            sx={{
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "full",
                padding: "2px",
                background:
                  "radial-gradient(transparent, transparent, rgba(255,255,255,0.5), transparent, transparent)",
                backgroundSize: "200% 200%",
                WebkitMask:
                  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                animation: `${shineSpin} 6s linear infinite`,
                pointerEvents: "none",
                zIndex: 3,
              },
            }}
          >
            <Image src={ceoPhoto} alt="Henrik Rydberg" w="100%" h="100%" objectFit="cover" />
          </Box>
          <Flex direction="column" align="center">
            <Text fontSize="16px" fontWeight="500" color="dune.dune">
              Henrik Rydberg
            </Text>
            <Text fontSize="13px" color="dune.500">
              CEO, GlobalComix
            </Text>
          </Flex>
        </Flex>

        {/* Right: quote */}
        <Flex direction="column" gap="20px" flex="1">
          <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
            From the CEO
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700" lineHeight="1.7">
            {QUOTE}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

function Option7b() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 7B — Circle Left + Quote Right (160px)"
        description="Same layout but bigger 160px headshot. More presence for Henrik."
      />
      <Flex maxW="960px" gap="48px" align="center" direction={{ base: "column", lg: "row" }}>
        <Flex direction="column" align="center" gap="16px" flexShrink={0}>
          <Box
            w="160px"
            h="160px"
            borderRadius="full"
            overflow="hidden"
            position="relative"
            border="2px solid rgba(255,255,255,0.15)"
            boxShadow="0 4px 20px rgba(0,0,0,0.4)"
            sx={{
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "full",
                padding: "2px",
                background:
                  "radial-gradient(transparent, transparent, rgba(255,255,255,0.5), transparent, transparent)",
                backgroundSize: "200% 200%",
                WebkitMask:
                  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                animation: `${shineSpin} 6s linear infinite`,
                pointerEvents: "none",
                zIndex: 3,
              },
            }}
          >
            <Image src={ceoPhoto} alt="Henrik Rydberg" w="100%" h="100%" objectFit="cover" />
          </Box>
          <Flex direction="column" align="center">
            <Text fontSize="16px" fontWeight="500" color="dune.dune">
              Henrik Rydberg
            </Text>
            <Text fontSize="13px" color="dune.500">
              CEO, GlobalComix
            </Text>
          </Flex>
        </Flex>

        <Flex direction="column" gap="20px" flex="1">
          <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
            From the CEO
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700" lineHeight="1.7">
            {QUOTE}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

function Option7c() {
  return (
    <Box bg="charcoal.800" borderRadius="24px" p="48px" border="1px solid" borderColor="whiteAlpha.100">
      <OptionLabel
        label="Option 7C — Circle Left + Quote Right (200px, big quote mark)"
        description="Larger 200px headshot with a decorative quote mark. Maximum personality."
      />
      <Flex maxW="960px" gap="48px" align="center" direction={{ base: "column", lg: "row" }}>
        <Flex direction="column" align="center" gap="16px" flexShrink={0}>
          <Box
            w="200px"
            h="200px"
            borderRadius="full"
            overflow="hidden"
            position="relative"
            border="2px solid rgba(255,255,255,0.15)"
            boxShadow="0 4px 20px rgba(0,0,0,0.4)"
            sx={{
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "full",
                padding: "2px",
                background:
                  "radial-gradient(transparent, transparent, rgba(255,255,255,0.5), transparent, transparent)",
                backgroundSize: "200% 200%",
                WebkitMask:
                  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                animation: `${shineSpin} 6s linear infinite`,
                pointerEvents: "none",
                zIndex: 3,
              },
            }}
          >
            <Image src={ceoPhoto} alt="Henrik Rydberg" w="100%" h="100%" objectFit="cover" />
          </Box>
          <Flex direction="column" align="center">
            <Text fontSize="16px" fontWeight="500" color="dune.dune">
              Henrik Rydberg
            </Text>
            <Text fontSize="13px" color="dune.500">
              CEO, GlobalComix
            </Text>
          </Flex>
        </Flex>

        <Flex direction="column" gap="16px" flex="1">
          <Text fontSize="48px" lineHeight="1" color="blaze.blaze" opacity={0.25}>
            &ldquo;
          </Text>
          <Text fontSize="20px" fontWeight="400" color="dune.700" lineHeight="1.7" mt="-16px">
            {QUOTE.replace("\u201C", "").replace("\u201D", "")}
          </Text>
          <Flex align="center" gap="12px" mt="4px">
            <Box w="24px" h="1px" bg="blaze.blaze" opacity={0.4} />
            <Text fontSize="14px" fontWeight="500" textTransform="uppercase" letterSpacing="0.1em" color="dune.500">
              From the CEO
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

/* ── Page layout ── */
export function HenrikTestPage() {
  return (
    <Box bg="charcoal.900" minH="100vh" py="64px" px="24px">
      <Box maxW="1100px" mx="auto">
        <Text fontSize="32px" fontWeight="600" color="dune.dune" mb="8px">
          Henrik Section — Layout Explorations
        </Text>
        <Text fontSize="16px" color="dune.500" mb="48px">
          9 layout options. Scroll to compare — new hybrids at the bottom.
        </Text>

        <Flex direction="column" gap="64px">
          <Option1 />
          <Option2 />
          <Option3 />
          <Option4 />
          <Option5 />
          <Option6 />
          <Option7a />
          <Option7b />
          <Option7c />
        </Flex>
      </Box>
    </Box>
  );
}
